import { useState } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { AuthShell, googleLogin } from "./AuthShell";
import { Btn, Field } from "@/components/ui-kit";
import { api, formatError } from "@/api";
import { useAuth } from "@/auth";
import { toast } from "sonner";
import { TERMS_VERSION } from "@/content/legal";

export default function Register() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const role = params.get("rooli");
  const { setUser } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [accepted, setAccepted] = useState(false);
  const [newsletter, setNewsletter] = useState(false);
  const [loading, setLoading] = useState(false);

  if (role !== "creator" && role !== "business") return <Navigate to="/valitse-rooli" replace />;

  const isCreator = role === "creator";
  const label = isCreator ? "sisällöntuottajana" : "yrityksenä";

  const submit = async (e) => {
    e.preventDefault();
    if (!accepted) {
      toast.error("Sinun tulee hyväksyä käyttöehdot ja tietosuojaseloste jatkaaksesi.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, role, accepted_terms: true, accepted_terms_version: TERMS_VERSION, newsletter_opt_in: newsletter });
      setUser(data);
      toast.success("Tervetuloa Creatoriin! Täydennä profiilisi aloittaaksesi.");
      navigate("/dashboard/profiili", { replace: true });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={`Rekisteröidy ${label}`} subtitle="Luo tili muutamassa sekunnissa." testid="register-page">
      <form onSubmit={submit} className="space-y-4">
        <Field label={isCreator ? "Nimi" : "Yrityksen nimi"} value={form.name} required
          onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-name" />
        <Field label="Sähköposti" type="email" value={form.email} required
          onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-email" />
        <Field label="Salasana" type="password" value={form.password} required minLength={6}
          onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-password" />
        <label className="flex items-start gap-3 text-sm text-[#6E675C] cursor-pointer select-none">
          <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)}
            data-testid="register-accept-terms"
            className="mt-0.5 h-4 w-4 rounded border-[#C7C0B5] text-[#292827] focus:ring-0 focus:ring-offset-0 accent-[#292827] shrink-0" />
          <span>
            Hyväksyn{" "}
            <a href="/kayttoehdot" target="_blank" rel="noopener noreferrer" className="font-medium text-[#292827] underline" data-testid="terms-link">käyttöehdot</a>
            {" "}ja{" "}
            <a href="/tietosuoja" target="_blank" rel="noopener noreferrer" className="font-medium text-[#292827] underline" data-testid="privacy-link">tietosuojaselosteen</a>.
          </span>
        </label>
        <label className="flex items-start gap-3 text-sm text-[#6E675C] cursor-pointer select-none">
          <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)}
            data-testid="register-newsletter"
            className="mt-0.5 h-4 w-4 rounded border-[#C7C0B5] text-[#292827] focus:ring-0 focus:ring-offset-0 accent-[#292827] shrink-0" />
          <span>Haluan tilata Creatorin uutiskirjeen (valinnainen).</span>
        </label>
        <Btn type="submit" disabled={loading} className="w-full" data-testid="register-submit">
          {loading ? "Luodaan..." : "Luo tili"}
        </Btn>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs text-[#968F85]">
        <div className="flex-1 h-px bg-[#E6E1D9]" /> TAI <div className="flex-1 h-px bg-[#E6E1D9]" />
      </div>
      <Btn variant="outline" className="w-full" onClick={googleLogin} data-testid="register-google">
        Jatka Googlella
      </Btn>
      <p className="mt-6 text-center text-sm text-[#6E675C]">
        Onko sinulla jo tili?{" "}
        <span className="font-medium text-[#292827] cursor-pointer hover:underline" onClick={() => navigate("/kirjaudu")}>Kirjaudu</span>
      </p>
    </AuthShell>
  );
}
